import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type MainView = 'chat' | 'settings' | 'help' | 'search' | 'terms';
export type Theme = 'light' | 'dark' | 'system';

interface UIState {
  sidebarOpen: boolean;
  currentView: MainView;
  theme: Theme;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setCurrentView: (view: MainView) => void;
  setTheme: (theme: Theme) => void;
}

// Apply theme to document
function applyTheme(theme: Theme) {
  const root = document.documentElement;

  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  } else {
    root.classList.toggle('dark', theme === 'dark');
  }
}

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const storedState = localStorage.getItem('ui-storage');
    if (storedState) {
      const { state } = JSON.parse(storedState);
      if (state?.theme === 'system') {
        applyTheme('system');
      }
    }
  });
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: false,
      currentView: 'chat',
      theme: 'system',
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setCurrentView: (view) => set({ currentView: view }),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        theme: state.theme,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply theme on initial load
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
    }
  )
);
